// src/components/DashboardLayout.js

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// --- DEPENDÊNCIAS DO MATERIAL-UI ---
import {
    Box, AppBar as MuiAppBar, Toolbar, IconButton, Drawer, List, ListItem,
    ListItemButton, ListItemIcon, ListItemText, Divider, Menu, MenuItem, CssBaseline,
    ThemeProvider, createTheme, styled, alpha, Tooltip, Avatar, Backdrop
} from '@mui/material';

// --- ÍCONES DO MATERIAL-UI ---
import {
    Menu as MenuIcon, Logout, Person, Home, ArrowBack as ArrowBackIcon, Insights,
    TrackChanges // ✅ 1. IMPORTE O ÍCONE NOVO
} from '@mui/icons-material';

import logoImage from '../assets/logonav.png'; 

const drawerWidth = 260;

const theme = createTheme({
    palette: { primary: { main: '#ff834f' } },
    typography: { fontFamily: "'Inter', sans-serif" }
});

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
    padding: '12px 24px',
    color: theme.palette.text.secondary,
    '&:hover': { backgroundColor: alpha(theme.palette.common.black, 0.04) },
    '&.Mui-selected': {
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
        color: theme.palette.primary.main,
        fontWeight: 'bold',
        '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.12) }
    },
}));


const DashboardLayout = ({ children }) => {
    const [open, setOpen] = useState(false);
    const [anchorElProfile, setAnchorElProfile] = useState(null);
    const auth = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // ✅ 2. ADICIONE O NOVO ITEM AO MENU
    const menuItems = [
        { text: 'Início', icon: <Home />, path: '/' },
        { text: 'Business Awareness', icon: <Insights />, path: '/business-awareness' },
        { text: 'Objetivos Estratégicos', icon: <TrackChanges />, path: '/strategic-objectives' },
    ];

    const handleDrawerToggle = () => setOpen(!open);
    const handleDrawerClose = () => setOpen(false);
    const handleProfileMenuOpen = (event) => setAnchorElProfile(event.currentTarget);
    const handleProfileMenuClose = () => setAnchorElProfile(null);
    const handleLogout = () => {
        handleProfileMenuClose();
        auth.logout();
    };

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ display: 'flex' }}>
                <CssBaseline />
                <MuiAppBar
                    position="fixed"
                    elevation={0}
                    sx={{
                        zIndex: (theme) => theme.zIndex.drawer + 1,
                        backgroundColor: '#050a30',
                        borderBottom: (theme) => `1px solid ${alpha(theme.palette.common.white, 0.1)}`
                    }}
                >
                    <Toolbar>
                        <IconButton
                            aria-label="toggle drawer"
                            onClick={handleDrawerToggle}
                            edge="start"
                            sx={{ color: '#FFFFFF', marginRight: 2 }}
                        >
                            <MenuIcon />
                        </IconButton>
                        
                        <a href="/" style={{ display: 'flex', alignItems: 'center' }}>
                            <Box component="img" sx={{ height: 40, display: { xs: 'none', sm: 'block' } }} alt="Logo" src={logoImage} />
                        </a>

                        <Box sx={{ flexGrow: 1 }} />

                        <Tooltip title="Opções de Perfil">
                            <IconButton onClick={handleProfileMenuOpen} size="small">
                                <Avatar
                                    sx={{ width: 32, height: 32 }}
                                    src={auth.user?.avatarUrl ? `${process.env.REACT_APP_UPLOADS_URL}/${auth.user.avatarUrl.replace(/\\/g, '/')}` : ''}
                                    alt={auth.user?.name ? auth.user.name[0].toUpperCase() : 'U'}
                                >
                                    {auth.user?.name ? auth.user.name[0].toUpperCase() : 'U'}
                                </Avatar>
                            </IconButton>
                        </Tooltip>
                        <Menu anchorEl={anchorElProfile} open={Boolean(anchorElProfile)} onClose={handleProfileMenuClose}>
                            <MenuItem onClick={() => { handleProfileMenuClose(); window.location.href = `${process.env.REACT_APP_COCKPIT_URL}perfil`; }}>
                                <ListItemIcon><Person fontSize="small" /></ListItemIcon>
                                Meu Perfil
                            </MenuItem>
                            <Divider />
                            <MenuItem onClick={handleLogout}><ListItemIcon><Logout fontSize="small" /></ListItemIcon>Sair</MenuItem>
                        </Menu>
                    </Toolbar>
                </MuiAppBar>

                <Drawer
                    anchor="left"
                    open={open}
                    onClose={handleDrawerClose}
                    variant="temporary"
                    ModalProps={{ keepMounted: true }}
                    sx={{ '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth } }}
                >
                    <Toolbar />
                    <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                        <List>
                            {menuItems.map((item) => (
                                <ListItem key={item.text} disablePadding>
                                    <StyledListItemButton onClick={() => navigate(item.path)} selected={location.pathname === item.path}>
                                        <ListItemIcon sx={{ color: location.pathname === item.path ? 'primary.main' : 'inherit' }}>
                                          {item.icon}
                                        </ListItemIcon>
                                        <ListItemText primary={item.text} />
                                    </StyledListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                    <Divider />
                    <List>
                       <ListItem disablePadding>
                         <StyledListItemButton onClick={() => window.location.href = process.env.REACT_APP_COCKPIT_URL}>
                             <ListItemIcon><ArrowBackIcon /></ListItemIcon>
                             <ListItemText primary="Voltar à Plataforma" />
                         </StyledListItemButton>
                      </ListItem>
                    </List>
                </Drawer>
                
                <Backdrop
                  sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer - 1 }}
                  open={open}
                  onClick={handleDrawerClose}
                />

                <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, sm: 3 }, width: '100%', minHeight: '100vh', bgcolor: '#f0f2f5' }}>
                    <Toolbar />
                    {children}
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default DashboardLayout;